// Doc-Ref: docs/features/collaborations-current.md
import { useState, useEffect, useMemo, useRef } from "react";
import {
  getCurrentCollaborationFacts,
  getCurrentCollaborationTable,
  getCurrentCollaborationMap,
  getCurrentCollaborationNetwork,
  getGeneralOverview
} from "Explore/Services/Collaborations";
import { Space, Tag, Skeleton } from "antd";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Card, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "components/ui/sheet";
import { Info, Globe, Table as TableIcon, Network, Search, Download, Building, User, Component as ComponentIcon, Loader2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
} from "recharts";
import InternationalCollaboratorMap from "Explore/Components/InternationalCollaboratorMap";
import USCollaboratorMap from "Explore/Components/USCollaboratorMap";
import D3Graph from "Explore/Components/Graph";
import { TIME_FILTER_OPTIONS } from "Explore/Config/constraint";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { getCorrectedText as globalGetCorrectedText, getRegionLabel } from "Explore/Config/regionData";
import InsufficientDataNotice from "../../Components/InsufficientDataNotice/InsufficientDataNotice";
import styles from "./TabCurrent.module.scss";

const TabCurrent = ({ filters = {}, setFilters, onDataEmpty, entity, isActive = false }) => {
  const metric = filters.metric || "publications";

  const getCorrectedText = (text, isEntityName = false) => globalGetCorrectedText(text, filters?.geography, isEntityName);

  const { filter: urlFilters } = useFilterAndPagination();
  const [viewMode, setViewMode] = useState("table");
  const [drawerPartner, setDrawerPartner] = useState(null);

  const [facts, setFacts] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [tableMeta, setTableMeta] = useState({ page: 1, pageSize: 10, totalItems: 0, sortBy: 'joint_pubs', sortOrder: 'desc' });
  const [mapData, setMapData] = useState([]);
  const [networkData, setNetworkData] = useState({ nodes: [], lines: [], max_edges: 0 });
  const [searchTerm, setSearchTerm] = useState("");

  const [loadingFacts, setLoadingFacts] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingMap, setLoadingMap] = useState(true);
  const [loadingNetwork, setLoadingNetwork] = useState(true);
  const [error, setError] = useState(null);
  const getBaseParams = () => ({
    entity_id: entity?.id,
    entity_type: entity?.type,
    publication_year_start: filters.yearRange?.[0],
    publication_year_end: filters.yearRange?.[1],
    keywords: filters.subjectArea && filters.subjectArea.length > 0 ? filters.subjectArea : undefined,
    keywords_operator: filters.keywordsOperator || 'or',
    geography: filters.geography,
    collaborator_type: filters.collaboratorType,
    node_count: urlFilters.top_k || 8,
  });

  const lastFetchedFactsParams = useRef(null);
  const lastFetchedTableParams = useRef(null);
  const lastFetchedMapParams = useRef(null);
  const lastFetchedNetworkParams = useRef(null);

  // Determine if main data is empty
  const currentParamsString = JSON.stringify(getBaseParams());
  const isStale = lastFetchedTableParams.current !== currentParamsString;
  const isMainDataEmpty = !isStale && (
    (viewMode === "table" && tableData.length === 0 && !loadingTable) ||
    (viewMode === "map" && mapData.length === 0 && !loadingMap) ||
    (viewMode === "network" && networkData.nodes.length === 0 && !loadingNetwork)
  );

  const [pubCount, setPubCount] = useState(null);
  const [loadingPubCount, setLoadingPubCount] = useState(false);

  useEffect(() => {
    if (!entity || !isActive) {
      setPubCount(null);
      return;
    }
    const fetchPubCount = async () => {
      setLoadingPubCount(true);
      try {
        const res = await getGeneralOverview(getBaseParams());
        const pubKpi = res.kpis?.find(k => (k.label || k.name || "").toLowerCase().includes("publication")) || res.kpis?.[0];
        const pubs = pubKpi?.value ?? 0;
        const numPubs = typeof pubs === 'string' ? parseInt(pubs.replace(/,/g, ''), 10) : Number(pubs);
        setPubCount(isNaN(numPubs) ? 0 : numPubs);
      } catch (err) {
        console.error("Failed to fetch pub count", err);
      } finally {
        setLoadingPubCount(false);
      }
    };
    fetchPubCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, entity, isActive]);

  const LOW_PUB_THRESHOLD = 10;
  const isLowData = !loadingPubCount
    && pubCount !== null
    && pubCount < LOW_PUB_THRESHOLD;

  useEffect(() => {
    if (onDataEmpty && isActive) {
      onDataEmpty(isMainDataEmpty);
    }
  }, [isMainDataEmpty, onDataEmpty, isActive]);

  // Fetch Facts (always shown)
  useEffect(() => {
    if (!entity || !isActive) return;
    const params = getBaseParams();
    const paramsString = JSON.stringify(params);
    if (lastFetchedFactsParams.current === paramsString) return;
    lastFetchedFactsParams.current = paramsString;

    const fetchFacts = async () => {
      setFacts([]);
      setLoadingFacts(true);
      setError(null);
      try {
        const res = await getCurrentCollaborationFacts(params);
        setFacts(res || []);
      } catch (err) {
        lastFetchedFactsParams.current = null;
        console.error("Failed to fetch facts:", err);
        setError(err.message || "Something went wrong.");
      } finally {
        setLoadingFacts(false);
      }
    };
    fetchFacts();
  }, [filters, entity, isActive]);

  // Fetch Table Data
  useEffect(() => {
    if (!entity || !isActive || viewMode !== "table") return;
    const params = {
      ...getBaseParams(),
      searchTerm,
      page: tableMeta.page,
      pageSize: tableMeta.pageSize,
      sortBy: tableMeta.sortBy,
      sortOrder: tableMeta.sortOrder,
    };
    const paramsString = JSON.stringify(params);
    if (lastFetchedTableParams.current === paramsString) return;
    lastFetchedTableParams.current = paramsString;

    const fetchTable = async () => {
      setTableData([]);
      setLoadingTable(true);
      try {
        const res = await getCurrentCollaborationTable(params);
        if (res) {
          setTableData(res.rows || []);
          setTableMeta(prev => ({ ...prev, totalItems: res.total_items || 0 }));
        }
      } catch (err) {
        lastFetchedTableParams.current = null;
        console.error("Failed to fetch table:", err);
      } finally {
        setLoadingTable(false);
      }
    };
    fetchTable();
  }, [filters, entity, searchTerm, tableMeta.page, tableMeta.pageSize, tableMeta.sortBy, tableMeta.sortOrder, viewMode, isActive]);

  // Fetch Map Data
  useEffect(() => {
    if (!entity || !isActive || viewMode !== "map") return;
    const params = getBaseParams();
    const paramsString = JSON.stringify(params);
    if (lastFetchedMapParams.current === paramsString) return;
    lastFetchedMapParams.current = paramsString;

    const fetchMap = async () => {
      setMapData([]);
      setLoadingMap(true);
      try {
        const res = await getCurrentCollaborationMap(params);
        if (res && res.items) {
          const isUsGeography = params.geography === "us";
          const formattedMapData = res.items.map(item => ({
            country_code: item.code,
            state_code: isUsGeography ? `US-${item.code}` : item.code,
            num_pubs: item.value,
            fwci: item.value,
            citations: item.value,
            collaborators: item.value,
            common_keywords: item.common_keywords || [],
            affiliations: (item.collaborators || []).map(collab => ({
              id: collab.id,
              institution_name: collab.name,
              num_pubs: collab.value,
              common_keywords: collab.common_keywords || [],
            })),
          }));
          setMapData(formattedMapData);
        }
      } catch (err) {
        lastFetchedMapParams.current = null;
        console.error("Failed to fetch map:", err);
      } finally {
        setLoadingMap(false);
      }
    };
    fetchMap();
  }, [filters, entity, viewMode, isActive]);

  // Fetch Network Data
  useEffect(() => {
    if (!entity || !isActive || viewMode !== "network") return;
    const params = getBaseParams();
    const paramsString = JSON.stringify(params);
    if (lastFetchedNetworkParams.current === paramsString) return;
    lastFetchedNetworkParams.current = paramsString;

    const fetchNetwork = async () => {
      setNetworkData({ nodes: [], lines: [], max_edges: 0 });
      setLoadingNetwork(true);
      try {
        const res = await getCurrentCollaborationNetwork(params);
        if (res) {
          const max_edges = res.max_edge ?? (res.edges?.length || 0);
          setNetworkData({
            nodes: (res.nodes || []).map(node => ({
              id: node.id,
              data: {
                name: node.name,
                type: node.type,
                value: node.value,
                common_keywords: node.common_keywords || [],
                photo: node.photo,
              }
            })),
            lines: (res.edges || []).map(edge => ({
              id: edge.id,
              source: edge.source,
              target: edge.target,
              weight: edge.value,
              common_keywords: edge.common_keywords || [],
              edge_type: edge.edge_type,
              shared_papers: edge.shared_papers,
            })),
            max_edges
          });
        }
      } catch (err) {
        lastFetchedNetworkParams.current = null;
        console.error("Failed to fetch network:", err);
      } finally {
        setLoadingNetwork(false);
      }
    };
    fetchNetwork();
  }, [filters, entity, viewMode, isActive, urlFilters.top_k]);

  // Map state
  const geography = filters.geography || "international";
  const [countries, setCountries] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [timeFilter, setTimeFilter] = useState(TIME_FILTER_OPTIONS[1].value);

  // Network filtering
  const filteredNetworkData = useMemo(() => {
    if (!networkData.nodes || networkData.nodes.length === 0) return networkData;

    const topKEdges = urlFilters.top_k_edges;

    // Backend already handles node filtering, we just use all nodes returned
    const topNodes = networkData.nodes;

    // Filter edges, sort by weight (descending) to prepare for edge limit
    let topLines = [...(networkData.lines || [])].sort((a, b) => (b.weight || 0) - (a.weight || 0));

    // Apply edge limit if provided
    if (topKEdges) {
      topLines = topLines.slice(0, topKEdges);
    }

    return {
      nodes: topNodes,
      lines: topLines,
      max_edges: networkData.max_edges
    };
  }, [networkData, urlFilters.top_k_edges]);

  // Table Columns
  const columns = [
    {
      title: "Partner Name",
      dataIndex: "partner_name",
      key: "partner_name",
      render: (text, record) => (
        <Button
          variant="link"
          className="p-0 h-auto font-semibold text-explore hover:opacity-80"
          onClick={() => setDrawerPartner(record)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: filters?.geography === "us" ? "State" : "Country",
      dataIndex: "region_code",
      key: "region_code",
      render: (text, record) => {
        return getRegionLabel(text, filters?.geography) || text;
      },
    },
    {
      title: "Joint Publications",
      dataIndex: "joint_pubs",
      key: "joint_pubs",
      render: (val) => (
        <span>{val?.toLocaleString()}</span>
      ),
    },
    {
      title: "FWCI",
      dataIndex: "fwci",
      key: "fwci",
    },
    {
      title: "Citations",
      dataIndex: "citations",
      key: "citations",
      render: (val) => val?.toLocaleString() || "-",
    },
    {
      title: "Avg Authors per Publication",
      dataIndex: "avg_authors_per_pub",
      key: "avg_authors_per_pub",
      render: (val) => val?.toLocaleString() || "-",
    },
    {
      title: `Trend (${filters.yearRange?.[0]}-${filters.yearRange?.[1]})`,
      dataIndex: "trend",
      key: "trend",
      render: (trendData) => (
        <div className={styles["sparkline-wrapper"]}>
          {trendData && trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData.map((v, i) => ({ val: v, idx: i }))}>
                <Line type="monotone" dataKey="val" stroke="#0f3460" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : "-"}
        </div>
      ),
    }
  ];

  const handleSort = (field) => {
    setTableMeta(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handlePageChange = (newPage) => {
    setTableMeta(prev => ({ ...prev, page: newPage }));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setTableMeta(prev => ({ ...prev, page: 1 }));
  };



  if (isMainDataEmpty && filters?.subjectArea?.length > 0) {
    return null;
  }

  return (
    <div className={styles["tab-current"]}>
      {/* Intro */}
      <div className={styles["tab-current__intro"]}>
        <p>
          Explore your active collaboration network. Current collaborations are defined by joint
          co-authorship on published outputs within the selected date range. Use the views below
          to evaluate partner productivity and citation impact.
        </p>
      </div>

      {/* Unified Insufficient Data Notice when publication < 10 */}
      {isLowData ? (
        <InsufficientDataNotice
          pubCount={pubCount}
          entity={entity}
          filters={filters}
          setFilters={setFilters}
          title="Insufficient Data to Detect Active Collaborations"
          description={
            <>
              The selected entity has only <strong className="font-semibold text-slate-900">{pubCount}</strong> publication{pubCount !== 1 ? 's' : ''} for the active filters. A minimum of 10 publications is required to detect collaboration partners and network signals.
            </>
          }
        />
      ) : (
        <>
          {/* Network Signals */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {loadingFacts ? (
              <div className="flex justify-center p-10 col-span-full">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : error ? (
              <div className="col-span-full">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </div>
          ) : facts.length === 0 ? (
            <div className="col-span-full">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Info</AlertTitle>
                <AlertDescription>No network signals available for the selected criteria.</AlertDescription>
              </Alert>
            </div>
          ) : (
            facts.map((fact) => {
              const isWarning = fact.type === "warning" || fact.type === "risk" || fact.type === "negative";
              return (
                <Card key={fact.id} className={isWarning ? "border-amber-500/50 bg-amber-50/30" : "bg-explore/5 border-explore/20"}>
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-xs font-semibold text-explore uppercase tracking-wider">{fact.label}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0 text-sm text-slate-700">
                    {fact.entity?.name ? (
                      (() => {
                        const correctedEntityName = getCorrectedText(fact.entity.name, true);
                        const originalEntityName = fact.entity.name;
                        let correctedDesc = getCorrectedText(fact.description);

                        if (correctedEntityName !== originalEntityName && originalEntityName.length === 2) {
                          correctedDesc = correctedDesc.replace(new RegExp(`\\b${originalEntityName}\\b`, 'g'), correctedEntityName);
                        }

                        const parts = correctedDesc.split(new RegExp(`(${correctedEntityName})`, 'gi'));
                        return (
                          <span>
                            {parts.map((part, i) => (
                              part.toLowerCase() === correctedEntityName.toLowerCase() ? (
                                <strong key={i} className="text-explore font-semibold">{part}</strong>
                              ) : (
                                part
                              )
                            ))}
                          </span>
                        );
                      })()
                    ) : (
                      getCorrectedText(fact.description)
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

      {/* View Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
        <Tabs value={viewMode} onValueChange={setViewMode} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="table" className="flex items-center gap-2 data-[state=active]:text-explore"><TableIcon className="w-4 h-4" /> Table View</TabsTrigger>
            <TabsTrigger value="map" className="flex items-center gap-2 data-[state=active]:text-explore"><Globe className="w-4 h-4" /> Map View</TabsTrigger>
            <TabsTrigger value="network" className="flex items-center gap-2 data-[state=active]:text-explore"><Network className="w-4 h-4" /> Network View</TabsTrigger>
          </TabsList>
        </Tabs>

        {viewMode === "table" && (
          <div className="relative w-full sm:w-[250px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search partners..."
              className="pl-9"
              defaultValue={searchTerm}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch(e.currentTarget.value);
              }}
              onBlur={(e) => handleSearch(e.currentTarget.value)}
            />
          </div>
        )}
      </div>

      {/* Viewport */}
      <div className={styles["viewport"]}>
        {viewMode === "table" && (
          <div className="bg-white flex flex-col">
            <div className="overflow-x-auto relative min-h-[300px]">
              {loadingTable && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {columns.map((col) => (
                      <TableHead
                        key={col.key}
                        className="whitespace-nowrap font-medium"
                      >
                        <div className="flex items-center space-x-1">
                          <span>{col.title}</span>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableData.length === 0 && !loadingTable ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-40 text-center p-6 text-slate-500">
                        No partners found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row) => (
                      <TableRow key={row.partnerId}>
                        {columns.map((col) => (
                          <TableCell key={col.key} className="whitespace-nowrap">
                            {col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {tableMeta.totalItems > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                <div className="text-sm text-slate-500">
                  Showing {((tableMeta.page - 1) * tableMeta.pageSize) + 1} to {Math.min(tableMeta.page * tableMeta.pageSize, tableMeta.totalItems)} of {tableMeta.totalItems} results
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={tableMeta.page === 1}
                    onClick={() => handlePageChange(tableMeta.page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {(() => {
                    const totalPages = Math.ceil(tableMeta.totalItems / tableMeta.pageSize);
                    const pages = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      if (tableMeta.page <= 4) {
                        pages.push(1, 2, 3, 4, 5, '...', totalPages);
                      } else if (tableMeta.page >= totalPages - 3) {
                        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                      } else {
                        pages.push(1, '...', tableMeta.page - 1, tableMeta.page, tableMeta.page + 1, '...', totalPages);
                      }
                    }

                    return pages.map((p, idx) => (
                      p === '...' ? (
                        <span key={`dots-${idx}`} className="px-2 text-slate-500">...</span>
                      ) : (
                        <Button
                          key={p}
                          variant="outline"
                          size="sm"
                          className={tableMeta.page === p ? "h-8 w-8 p-0 bg-explore text-white hover:bg-explore/90 hover:text-white border-explore" : "h-8 w-8 p-0"}
                          onClick={() => handlePageChange(p)}
                        >
                          {p}
                        </Button>
                      )
                    ));
                  })()}

                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={tableMeta.page * tableMeta.pageSize >= tableMeta.totalItems}
                    onClick={() => handlePageChange(tableMeta.page + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {viewMode === "map" && (
          <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e8e8e8" }}>
            {geography === "international" ? (
              <InternationalCollaboratorMap
                data={mapData}
                metric={metric}
                isLoading={loadingMap}
                countries={countries}
                setCountries={setCountries}
                selectedKeywords={selectedKeywords}
                setSelectedKeywords={setSelectedKeywords}
                timeFilter={timeFilter}
                setTimeFilter={setTimeFilter}
              />
            ) : (
              <USCollaboratorMap
                data={mapData}
                metric={metric}
                isLoading={loadingMap}
                selectedKeywords={selectedKeywords}
                setSelectedKeywords={setSelectedKeywords}
                timeFilter={timeFilter}
                setTimeFilter={setTimeFilter}
              />
            )}
          </div>
        )}
        {viewMode === "network" && (
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", height: "700px", overflow: "hidden" }}>
            <D3Graph
              data={filteredNetworkData}
              isLoading={loadingNetwork}
              graphType="institution"
              filter={urlFilters}
              height="100%"
            />
          </div>
        )}
      </div>
        </>
      )}

      <Sheet open={!!drawerPartner} onOpenChange={(open) => !open && setDrawerPartner(null)}>
        <SheetContent side="right" className="w-[600px] sm:max-w-[600px] overflow-y-auto bg-white">
          <SheetHeader className="mb-6 border-b pb-4">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-xl">Partner Profile</SheetTitle>
            </div>
          </SheetHeader>

          {drawerPartner && (
            <div className="mt-4">
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                  {drawerPartner.photo || drawerPartner.avatar || drawerPartner.logo || drawerPartner.image ? (
                    <img src={drawerPartner.photo || drawerPartner.avatar || drawerPartner.logo || drawerPartner.image} alt={drawerPartner.name} className="w-full h-full object-contain" />
                  ) : drawerPartner.type === "institution" ? (
                    <Building className="w-10 h-10 text-slate-400" />
                  ) : drawerPartner.type === "unit" ? (
                    <ComponentIcon className="w-10 h-10 text-slate-400" />
                  ) : (
                    <User className="w-10 h-10 text-slate-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-1">{drawerPartner.partner_name}</h2>
                  <p className="text-slate-500 text-sm">{getRegionLabel(drawerPartner.region_code, filters?.geography) || drawerPartner.region_code}</p>
                </div>
              </div>

              <div className="mt-8 p-6 rounded-lg border border-slate-200 bg-slate-50">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Collaboration Summary</h3>
                <div className="flex gap-12">
                  <div>
                    <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">Joint Publications</div>
                    <div className="text-3xl font-light text-slate-900 tabular-nums">{drawerPartner.joint_pubs?.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">FWCI</div>
                    <div className="text-3xl font-light text-slate-900 tabular-nums">{drawerPartner.fwci}</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-12 border border-dashed border-slate-200 rounded-lg flex items-center justify-center text-slate-400">
                [Timeline Chart Placeholder]
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default TabCurrent;
